/**
 * Live State Indicator Component
 * 
 * Visual indicator showing that the system is live and changes propagate immediately.
 * Distinguishes control surfaces from passive dashboards.
 * 
 * @see docs/CONTROL_SURFACE_VS_DASHBOARD.md - Control Surface vs Dashboard distinction
 */

import React, { useState, useEffect } from 'react';
import { Badge, Tooltip, Typography } from 'antd';
import { SyncOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import styles from './LiveStateIndicator.module.css';

const { Text } = Typography;

export type ConnectionStatus = 'connected' | 'syncing' | 'disconnected';

interface LiveStateIndicatorProps {
  /** Current connection/sync status */
  status?: ConnectionStatus;
  /** Last update timestamp */
  lastUpdate?: Date;
  /** Whether to show the last update time */
  showLastUpdate?: boolean;
  /** Whether in compact mode */
  compact?: boolean;
  /** Custom class name */
  className?: string;
}

const STATUS_CONFIG: Record<ConnectionStatus, { color: string; label: string; icon: React.ReactNode }> = {
  connected: {
    color: '#10b981',
    label: 'Live',
    icon: <CheckCircleOutlined />,
  },
  syncing: {
    color: '#f59e0b',
    label: 'Syncing',
    icon: <SyncOutlined spin />,
  },
  disconnected: {
    color: '#ef4444',
    label: 'Disconnected',
    icon: <ExclamationCircleOutlined />,
  },
};

const formatTimeAgo = (date: Date): string => {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  
  if (seconds < 5) return 'Just now';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return date.toLocaleDateString();
};

export const LiveStateIndicator: React.FC<LiveStateIndicatorProps> = ({
  status = 'connected',
  lastUpdate = new Date(),
  showLastUpdate = true,
  compact = false,
  className,
}) => {
  const [, setTick] = useState(0);
  const config = STATUS_CONFIG[status];

  // Update time display every 10 seconds
  useEffect(() => {
    if (!showLastUpdate) return;
    
    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, 10000);
    
    return () => clearInterval(interval);
  }, [showLastUpdate]);

  const containerClasses = [
    styles.container,
    compact && styles.compact,
    className,
  ].filter(Boolean).join(' ');

  const tooltipContent = (
    <div>
      <div>Status: {config.label}</div>
      {showLastUpdate && <div>Last update: {formatTimeAgo(lastUpdate)}</div>}
      <div style={{ marginTop: 4, opacity: 0.8, fontSize: 11 }}>
        Changes propagate immediately to execution
      </div>
    </div>
  );

  return (
    <Tooltip title={tooltipContent}>
      <div className={containerClasses}>
        <Badge
          status={status === 'syncing' ? 'processing' : status === 'connected' ? 'success' : 'error'}
          className={styles.badge}
        />
        <div className={styles.content}>
          <span className={styles.icon} style={{ color: config.color }}>
            {config.icon}
          </span>
          {!compact && (
            <>
              <Text className={styles.label} style={{ color: config.color }}>
                {config.label}
              </Text>
              {showLastUpdate && (
                <Text type="secondary" className={styles.timestamp}>
                  {formatTimeAgo(lastUpdate)}
                </Text>
              )}
            </>
          )}
        </div>
      </div>
    </Tooltip>
  );
};

export default LiveStateIndicator;
